import React, { useState } from "react";
import Modal from "./Modal";
import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { LogIn, UserPlus, KeyRound, Mail, Loader2, Eye, EyeOff } from "lucide-react";

const Field = ({ label, ...props }) => (
  <label className="block">
    <span className="text-sm text-apex-cream/75 mb-1.5 inline-block">{label}</span>
    <input
      {...props}
      className="w-full px-4 py-3 rounded-xl bg-apex-bg border border-apex-line focus:border-apex-gold focus:outline-none focus:ring-2 focus:ring-apex-gold/30 text-apex-cream placeholder:text-apex-cream/40 transition-all"
    />
  </label>
);

const PasswordField = ({ label, ...props }) => {
  const [visible, setVisible] = useState(false);
  return (
    <label className="block">
      <span className="text-sm text-apex-cream/75 mb-1.5 inline-block">{label}</span>
      <div className="relative">
        <input
          {...props}
          type={visible ? "text" : "password"}
          className="w-full px-4 py-3 pr-12 rounded-xl bg-apex-bg border border-apex-line focus:border-apex-gold focus:outline-none focus:ring-2 focus:ring-apex-gold/30 text-apex-cream placeholder:text-apex-cream/40 transition-all"
        />
        <button
          type="button"
          tabIndex={-1}
          onClick={() => setVisible((v) => !v)}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-apex-cream/50 hover:text-apex-gold transition-colors"
          aria-label={visible ? "Skrýt heslo" : "Zobrazit heslo"}
          data-testid="password-toggle-btn"
        >
          {visible ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
        </button>
      </div>
    </label>
  );
};

export default function AuthModal({ open, onClose, defaultMode = "login" }) {
  const { login, register } = useAuth();
  const [mode, setMode] = useState(defaultMode); // login | register | forgot | reset
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [form, setForm] = useState({ email: "", password: "", firstName: "", lastName: "", phone: "" });
  const [resetForm, setResetForm] = useState({ email: "", code: "", newPassword: "" });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setError(""); setSuccess("");
    try {
      if (mode === "login") {
        await login(form.email, form.password);
        onClose?.();
      } else if (mode === "register") {
        if (!form.password || form.password.length < 6) {
          throw new Error("Heslo musí mít alespoň 6 znaků.");
        }
        if (!form.phone || form.phone.trim().length < 6) {
          throw new Error("Vyplňte prosím telefonní číslo (potřebujeme ho pro domluvení termínu).");
        }
        const { getCurrentUtm } = await import("./AnalyticsTracker");
        await register({
          email: form.email,
          password: form.password,
          firstName: form.firstName,
          lastName: form.lastName,
          phone: form.phone,
          utm: getCurrentUtm() || null,
        });
        onClose?.();
      } else if (mode === "forgot") {
        const { data } = await api.post(`/auth/forgot-password?email=${encodeURIComponent(resetForm.email)}`);
        setSuccess(data?.message || "Pokud email existuje, byl odeslán resetovací kód.");
        setMode("reset");
      } else if (mode === "reset") {
        if (!resetForm.newPassword || resetForm.newPassword.length < 6) {
          throw new Error("Nové heslo musí mít alespoň 6 znaků.");
        }
        await api.post(`/auth/reset-password?email=${encodeURIComponent(resetForm.email)}&reset_code=${encodeURIComponent(resetForm.code)}&new_password=${encodeURIComponent(resetForm.newPassword)}`);
        setSuccess("Heslo bylo úspěšně změněno. Můžete se přihlásit.");
        setMode("login");
      }
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || "Něco se nepovedlo");
    } finally {
      setLoading(false);
    }
  };

  const title = mode === "login" ? "Přihlášení" : mode === "register" ? "Registrace" : mode === "forgot" ? "Zapomenuté heslo" : "Reset hesla";

  return (
    <Modal open={open} onClose={onClose} title={title} size="sm" testId="auth-modal" disableOutsideClose>
      <form onSubmit={submit} className="space-y-4">
        {error && <div className="text-sm text-red-400 bg-red-500/10 border border-red-500/30 rounded-lg p-3" data-testid="auth-error">{error}</div>}
        {success && <div className="text-sm text-emerald-300 bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-3">{success}</div>}

        {mode === "register" && (
          <div className="grid grid-cols-2 gap-3">
            <Field label="Jméno" value={form.firstName} onChange={(e) => setForm({ ...form, firstName: e.target.value })} required data-testid="auth-firstname-input" />
            <Field label="Příjmení" value={form.lastName} onChange={(e) => setForm({ ...form, lastName: e.target.value })} required data-testid="auth-lastname-input" />
          </div>
        )}

        {(mode === "login" || mode === "register") && (
          <>
            <Field label="Email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required data-testid="auth-email-input" />
            <PasswordField label="Heslo" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required minLength={6} data-testid="auth-password-input" />
            {mode === "register" && (
              <Field label="Telefon" type="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} required minLength={6} placeholder="Potřebujeme ho pro domluvení termínu" data-testid="auth-phone-input" />
            )}
          </>
        )}

        {mode === "forgot" && (
          <Field label="Email" type="email" value={resetForm.email} onChange={(e) => setResetForm({ ...resetForm, email: e.target.value })} required data-testid="forgot-email-input" />
        )}

        {mode === "reset" && (
          <>
            <Field label="Email" type="email" value={resetForm.email} onChange={(e) => setResetForm({ ...resetForm, email: e.target.value })} required data-testid="reset-email-input" />
            <Field label="6-místný kód" value={resetForm.code} onChange={(e) => setResetForm({ ...resetForm, code: e.target.value })} required maxLength={6} data-testid="reset-code-input" />
            <PasswordField label="Nové heslo" value={resetForm.newPassword} onChange={(e) => setResetForm({ ...resetForm, newPassword: e.target.value })} required minLength={6} data-testid="reset-newpassword-input" />
          </>
        )}

        <button disabled={loading} type="submit" className="btn-gold w-full !justify-center" data-testid="auth-submit-btn">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : (
            mode === "login" ? <><LogIn className="w-4 h-4" /> Přihlásit se</> :
            mode === "register" ? <><UserPlus className="w-4 h-4" /> Registrovat</> :
            mode === "forgot" ? <><Mail className="w-4 h-4" /> Odeslat kód</> :
            <><KeyRound className="w-4 h-4" /> Změnit heslo</>
          )}
        </button>

        <div className="text-sm text-center text-apex-cream/70 space-y-1">
          {mode === "login" && (
            <>
              <div>Nemáte účet? <button type="button" onClick={() => setMode("register")} className="text-apex-gold hover:underline" data-testid="switch-to-register">Vytvořit účet</button></div>
              <div><button type="button" onClick={() => setMode("forgot")} className="text-apex-cream/55 hover:text-apex-gold" data-testid="switch-to-forgot">Zapomněli jste heslo?</button></div>
            </>
          )}
          {mode === "register" && (
            <div>Už máte účet? <button type="button" onClick={() => setMode("login")} className="text-apex-gold hover:underline" data-testid="switch-to-login">Přihlásit se</button></div>
          )}
          {(mode === "forgot" || mode === "reset") && (
            <div><button type="button" onClick={() => setMode("login")} className="text-apex-cream/65 hover:text-apex-gold">← Zpět k přihlášení</button></div>
          )}
        </div>
      </form>
    </Modal>
  );
}
